import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X } from "lucide-react";

/**
 * SignupPromoModal — one-time celebration surface shown to students whose
 * signup response includes a `promo` payload (e.g. Oconomowoc High School's
 * 5-free-credits offer). Silent everywhere else on the site — this is the
 * ONLY place we announce the perk.
 *
 * Props:
 *   promo    — { code, credits, title, subtitle, message, school_name } | null
 *   onClose  — invoked when the user dismisses or clicks the CTA
 */
export function SignupPromoModal({ promo, onClose }) {
  return (
    <AnimatePresence>
      {promo && (
        <>
          {/* backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[110] bg-ink/70 backdrop-blur-sm"
            onClick={onClose}
            data-testid="signup-promo-backdrop"
          />
          {/* dialog */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }}
            className="fixed inset-0 z-[111] flex items-center justify-center p-4 pointer-events-none"
            role="dialog"
            aria-modal="true"
            aria-labelledby="promo-title"
          >
            <div
              data-testid="signup-promo-modal"
              className="pointer-events-auto relative w-full max-w-[520px] bg-ivory rounded-sm overflow-hidden shadow-2xl border border-champagne/40"
            >
              {/* close */}
              <button
                onClick={onClose}
                aria-label="Close"
                data-testid="signup-promo-close"
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-ink/5 hover:bg-ink/10 flex items-center justify-center text-ink/60 hover:text-ink transition-colors z-10"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Editorial header — dark linen with a warm gradient wash */}
              <div className="relative bg-ink text-cream px-8 pt-10 pb-8 overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-60 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse at top left, rgba(183,110,121,.55) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(212,169,74,.35) 0%, transparent 60%)",
                  }}
                />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream/10 border border-cream/25">
                    <Sparkles className="w-3.5 h-3.5 text-champagne" strokeWidth={1.75} />
                    <span className="font-label text-[9px] tracking-[0.3em] text-champagne">
                      OCONOMOWOC · STUDENT PERK
                    </span>
                  </div>
                  <h2
                    id="promo-title"
                    data-testid="signup-promo-title"
                    className="font-display italic text-4xl md:text-5xl leading-[1.05] mt-5 text-cream"
                  >
                    {promo.title || "Welcome!"}
                  </h2>
                  <p
                    className="font-body text-cream/80 mt-2 text-sm"
                    data-testid="signup-promo-subtitle"
                  >
                    {promo.subtitle || `You've unlocked ${promo.credits} free AI Try-On credits`}
                  </p>
                </div>
              </div>

              {/* Body */}
              <div className="px-8 py-7 space-y-5">
                {/* Credit chip */}
                <div className="flex items-center gap-4 bg-champagne/20 border border-champagne/60 rounded-sm px-5 py-4">
                  <div className="w-14 h-14 rounded-full bg-ink text-champagne flex items-center justify-center shrink-0">
                    <span
                      className="font-display text-3xl leading-none"
                      data-testid="signup-promo-credits"
                    >
                      {promo.credits}
                    </span>
                  </div>
                  <div>
                    <p className="font-label text-[9px] tracking-[0.28em] text-rosemetal">
                      GIFTED CREDITS
                    </p>
                    <p className="font-body text-sm text-ink/80 mt-0.5">
                      Free AI Try-Ons — see any dress on you before you lock it in.
                    </p>
                  </div>
                </div>

                <p
                  className="font-body text-[15px] text-ink/75 leading-relaxed"
                  data-testid="signup-promo-message"
                >
                  {promo.message}
                </p>

                {/* CTA */}
                <button
                  onClick={onClose}
                  data-testid="signup-promo-cta"
                  className="w-full bg-ink text-cream py-4 font-label text-[10px] tracking-[0.3em] rounded-sm hover:bg-rosemetal transition-colors"
                >
                  START TRYING ON →
                </button>
                <p className="font-label text-[9px] tracking-[0.24em] text-ink/40 text-center">
                  VERIFY YOUR SCHOOL EMAIL TO USE YOUR CREDITS
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
